import { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMenu } from "../../utils/context/MenuContext";
import { StaticRange } from "react-date-range";
import { Range } from "react-date-range";
import { subMonths, startOfMonth, endOfMonth } from "date-fns";
import CustomDropDownMenu from "../../components/common/CustomDropDownMenu";
import { GetImages } from "../../utils/GetImages";
import FilterDropdown from "./filterDropdown";
import TransactionTable from "./TransactionTable";
import apiHelper from "../../utils/apiHelper";
import {
  DateFilterTypesEnum,
  EntityType,
  FilterEntityEnum,
  OrganizationService,
  PaymentService,
} from "@propertelligent/client-api";
import { UserLoginData } from "../../components/common/charts/Enums";
import DateHelper from "../../utils/DateHelper";
import { GeneratePdfContext } from "../../utils/context/GeneratePdfContext";
import FlatViewTable from "./FlatViewTable";
import AddTraspButton from "../../components/firstTimeUserExperience/commonCom/AddTraspButton";
import { infiniteScrollHook } from "../../hooks/infiniteScrollHook";
import { Routes, Route, useLocation } from "react-router-dom";
import CustomRangeFilter from "../customDateRangeFilter/CustomRangeFilter";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import SimpleSpinner from "../../components/loader";
import { downloadBlob, exportTransactionsToExcel } from "../../services/transactionExport.service";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type SelectedCategories = { [key: string]: string[] };

interface TransactionViewProps {
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: number;
  tenatPadding?: string;
  isContractor?: boolean;
  contractorPayeeAccountNames?: string[];
  // Notifies the detail page's own header (Past Due/Income Due) to refetch after a transaction
  // delete -- see FlatViewTable.tsx/TransactionTable.tsx for the full rationale
  // (bug/ui-bugfix-batch8 item 5 follow-up).
  onRefresh?: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Build the dateFilter params object used by both flat-view hook and grouped calls. */
export const buildDateParams = (relativeDateString: string, range: Range[]) => {
  const hasDate = relativeDateString !== "";
  // Every preset this UI offers (Last 30 Days / Last 3 Months / Last 6 Months /
  // Last Year / Custom) is always sent as an explicit Custom range using the
  // client-computed start/end dates in `range` -- CustomRangeFilter's
  // handleFilterClick keeps `range` correctly in sync for every preset click,
  // not just the calendar-picker "Custom" flow. Some of the backend's own named
  // DateFilterTypesEnum values compute a different range than this UI's presets
  // intend -- DateFilterTypesEnum.LastMonth is the entire previous calendar
  // month, and DateFilterTypesEnum.LastYear is the entire previous calendar
  // year -- so resolving relativeDateString to one of those enum values and
  // letting the backend compute the range itself silently applies the wrong
  // window. Always sending Custom with the already-correct client-computed
  // dates sidesteps that mismatch entirely (see bug/tx-filter-clip-30d).
  // The backend truncates DateFilterVM.EndDate down to midnight of its calendar
  // date for Custom filters (DateFilterBM.GetFilterDateRange's Custom case uses
  // EndDate.Date, discarding whatever time-of-day was sent) and then compares
  // PaymentDate <= EndDate directly -- so a transaction that happened later that
  // same day (any time after midnight) fails the <= check and is silently
  // excluded. There is no time-of-day that avoids this once truncated to
  // midnight; the boundary itself has to move to the start of the NEXT day so
  // the post-truncation midnight is inclusive of the entire intended last day.
  const endBoundary = range[0]?.endDate ?? range[0]?.startDate;
  const nextDayBoundary = endBoundary
    ? new Date(endBoundary.getFullYear(), endBoundary.getMonth(), endBoundary.getDate() + 1)
    : undefined;
  return {
    dateFilterDateFilterTypeId: hasDate ? DateFilterTypesEnum.Custom : undefined,
    dateFilterClientDate: hasDate ? DateHelper.getClientDate() : undefined,
    dateFilterStartDate: hasDate
      ? DateHelper.getLocalISOString(range[0].startDate)
      : null,
    dateFilterEndDate: hasDate && nextDayBoundary
      ? DateHelper.getLocalISOString(nextDayBoundary)
      : null,
    dateFilterDateField: hasDate ? "PaymentDate" : undefined,
  };
};

const FLAT_VIEW_NAME = "Flat View";
const GROUPED_VIEW_NAME = "Grouped View";
const EXPORT_FAILURE_MESSAGE = "Unable to export transactions to Excel.";

export const buildTransactionsExportFileName = (date = new Date()) =>
  `Transactoins-${new Intl.DateTimeFormat("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  }).format(date).replaceAll("/", "-")}.xlsx`;

/**
 * Resolves the flat-view Excel export request. Only Flat View is exportable --
 * the export button is hidden entirely while Grouped View is selected (see
 * `canShowExportButton && selectedView?.name === FLAT_VIEW_NAME` at the call
 * site), so this never needs to resolve a `tx-groups/excel` route.
 */
export const resolveTransactionExportRequest = ({
  isEntity,
  entityId,
  entityTypeId,
  orgId,
  appliedFilters,
  dateParams,
}: {
  isEntity?: boolean;
  entityId?: number;
  entityTypeId?: number;
  orgId: number;
  appliedFilters: {
    fullyQualifiedFilters: string | null;
    filterEntityTypeId: FilterEntityEnum | undefined;
    filterEntityIds: number | number[] | undefined;
  };
  dateParams: ReturnType<typeof buildDateParams>;
}) => {
  const queryParams = {
    PageSize: undefined,
    PageNumber: undefined,
    SkipValue: undefined,
    FilterEntityTypeId: appliedFilters.filterEntityTypeId,
    FilterEntityIds: Array.isArray(appliedFilters.filterEntityIds)
      ? appliedFilters.filterEntityIds
      : appliedFilters.filterEntityIds != null
        ? [appliedFilters.filterEntityIds]
        : undefined,
    FullyQualifiedFilters: appliedFilters.fullyQualifiedFilters ?? undefined,
    FullyQualifiedSorts: undefined,
    "DateFilter.DateFilterTypeId": dateParams.dateFilterDateFilterTypeId,
    "DateFilter.ClientDate": dateParams.dateFilterClientDate,
    "DateFilter.StartDate": dateParams.dateFilterStartDate,
    "DateFilter.EndDate": dateParams.dateFilterEndDate,
    "DateFilter.DateField": dateParams.dateFilterDateField,
    "DateFilter.Formatted": undefined,
    Formatted: undefined,
  };

  if (!isEntity) {
    return {
      path: `/api/OrganizationTransaction/${orgId}/organizationlist/excel`,
      queryParams,
    };
  }

  switch (entityTypeId) {
    case EntityType.Property:
      return {
        path: `/api/OrganizationTransaction/${entityId}/propertylist/excel`,
        queryParams,
      };
    case EntityType.Unit:
      return {
        path: `/api/OrganizationTransaction/${entityId}/unitlist/excel`,
        queryParams,
      };
    case EntityType.Tenant:
      return {
        path: `/api/OrganizationTransaction/${entityId}/tenantlist/excel`,
        queryParams,
      };
    default:
      return {
        path: `/api/OrganizationTransaction/${orgId}/organizationlist/excel`,
        queryParams,
      };
  }
};

/** Build FullyQualifiedFilters string from selectedCategories + optional contractor names. */
const buildFQF = (
  selectedCategories: SelectedCategories,
  isContractor: boolean,
  contractorPayeeAccountNames?: string[]
): string | null => {
  const filterClauses = Object.entries(selectedCategories)
    .filter(([, values]) => values && values.length > 0)
    .map(([column, values]) => {
      const valueClauses = values.map((v) => `${column}=="${v}"`).join(" or ");
      return `(${valueClauses})`;
    });


  if (isContractor) {
    if (contractorPayeeAccountNames && contractorPayeeAccountNames.length > 0) {
      const contractorFilter = contractorPayeeAccountNames
        .map((name) => `payeeAccount.accountName=="${name}"`)
        .join(" or ");
      filterClauses.push(`(${contractorFilter})`);
    } else {
      filterClauses.push(`(payeeAccount.accountName=="")`);
    }
  }

  return filterClauses.length > 0 ? filterClauses.join(" and ") : null;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const TransactionView = ({
  isEntity,
  entityId,
  entityTypeId,
  tenatPadding,
  isContractor = false,
  contractorPayeeAccountNames,
  onRefresh,
}: TransactionViewProps) => {
  const { isOpen } = useMenu();
  const orgId = parseInt(sessionStorage.getItem(UserLoginData.organization_id));

  // `isContractor` is shared by two different callers: (1) a Contractor's own dashboard
  // (contractorDashboardTabs.tsx) -- a true Contractor JWT session with no orgId in
  // sessionStorage, and (2) an OrgAdmin viewing a specific contractor's detail page
  // (transaction-tab-content.tsx, via ContractorDetail.tsx) -- an OrgAdmin session that DOES
  // carry orgId, which narrows an org-wide query down to one contractor via
  // contractorPayeeAccountNames/FullyQualifiedFilters instead. Only the former should route to
  // the dedicated contractorlist endpoint below -- routing case (2) there too would send the
  // OrgAdmin's own orgId to the backend as if it were a contractorId.
  const isContractorSelfView = isContractor && (!orgId || isNaN(orgId));

  // ── Context ────────────────────────────────────────────────────────────────
  const {
    selectedOption,
    selectedIds,
    setSelectedOption,
    setSelectedIds,
    setSelectedValues,
    selectedValues,
  } = useContext(GeneratePdfContext);

  // ── Filter state ───────────────────────────────────────────────────────────
  const [selectedCategories, setSelectedCategories] =
    useState<SelectedCategories>({});

  const lastAppliedCategories = useRef<SelectedCategories>({});

  /**
   * `appliedFilters` is the single source of truth that actually drives API
   * calls. It is only updated when the user explicitly clicks "Apply filter"
   * or "Clear filter". This prevents stale-closure / cascade issues.
   */
  const [appliedFilters, setAppliedFilters] = useState<{
    fullyQualifiedFilters: string | null;
    filterEntityTypeId: FilterEntityEnum | undefined;
    filterEntityIds: number | number[] | undefined;
  }>({
    fullyQualifiedFilters: isContractor
      ? buildFQF({}, !isContractorSelfView, contractorPayeeAccountNames)
      : null,
    filterEntityTypeId: isContractor ? undefined : (isEntity
      ? (entityTypeId as FilterEntityEnum)
      : FilterEntityEnum.Organization),
    filterEntityIds: isContractor ? undefined : (isEntity
      ? entityId
      : orgId),
  });

  const [fetchTrigger, setFetchTrigger] = useState(0);

  useEffect(() => {
    if (isContractor) {
      setAppliedFilters((prev) => {
        const newFqf = buildFQF(lastAppliedCategories.current, !isContractorSelfView, contractorPayeeAccountNames);
        if (prev.fullyQualifiedFilters !== newFqf) {
          return { ...prev, fullyQualifiedFilters: newFqf };
        }
        return prev;
      });
      // Also trigger fetch when contractor context changes
      setFetchTrigger(t => t + 1);
    }
  }, [isContractor, contractorPayeeAccountNames]);

  // ── Date filter state ──────────────────────────────────────────────────────
  const [relativeDateString, setRelativeDateString] = useState("");
  const [range, setRange] = useState<Range[]>([
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ]);

  // ── View state ─────────────────────────────────────────────────────────────
  const viewOptions = useMemo(
    () => [
      { id: 1, img: GetImages.FlatViewIcon, name: "Flat View" },
      { id: 2, img: GetImages.GroupedView, name: "Grouped View" },
    ],
    []
  );
  const [selectedView, setSelectedView] = useState(viewOptions[0]);
  const [show, setShow] = useState(false);

  // ── Filter meta data (dropdowns in FilterDropdown) ─────────────────────────
  const [filterData, setFilterData] = useState<any>();
  const [paymentMethodsArr, setPaymentMethodsArr] = useState([]);
  const [payeeAccountData, setPayeeAccountData] = useState([]);
  const canManageTransactions =
    !isContractor && entityTypeId !== EntityType.Tenant;
  const canManageGroupedTransactions = canManageTransactions && !isEntity;

  // ── Grouped view data ──────────────────────────────────────────────────────
  const [organizationDataTX, setOrganizationDataTX] = useState([]);
  const [organizationData, setOrganizationData] = useState([]);
  const [propertyData, setPropertyData] = useState([]);
  const [unitData, setUnitData] = useState([]);
  const [propertyDataTX, setPropertyDataTX] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [selectedUnitId, setSelectedUnitId] = useState<number | null>(null);

  // Loading states for grouped view
  const [orgLoading, setOrgLoading] = useState(false);
  const [propertyLoading, setPropertyLoading] = useState(false);
  const [unitLoading, setUnitLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // ── Custom date range options ───────────────────────────────────────────────
  const customRanges = useMemo(
    () => [
      {
        label: "Last Month",
        isSelected: () => false,
        range: () => ({
          startDate: startOfMonth(subMonths(new Date(), 1)),
          endDate: endOfMonth(subMonths(new Date(), 1)),
        }),
      },
      {
        label: "Last 3 Months",
        isSelected: () => false,
        range: () => ({
          startDate: startOfMonth(subMonths(new Date(), 4)),
          endDate: endOfMonth(subMonths(new Date(), 1)),
        }),
      },
      {
        label: "Last 6 Months",
        isSelected: () => false,
        range: () => ({
          startDate: startOfMonth(subMonths(new Date(), 7)),
          endDate: endOfMonth(subMonths(new Date(), 1)),
        }),
      },
      {
        label: "Last Year",
        isSelected: () => false,
        range: () => ({
          startDate: new Date(new Date().getFullYear() - 1, 0, 1),
          endDate: new Date(new Date().getFullYear() - 1, 11, 31),
        }),
      },
      {
        label: "Custom Range",
        isSelected: () => false,
        range: () => ({
          startDate: range[0].startDate,
          endDate: range[0].endDate,
        }),
      },
    ],
    [range]
  );

  // ── Dropdown menu items for view switcher ──────────────────────────────────
  const viewMenuData = useMemo(
    () =>
      viewOptions.map((v) => ({
        ...v,
        textClass: "normal_text black",
        onclick: () => handleSelectView(v),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // ── Validate before API calls (property/unit must have IDs selected) ────────
  const validateApiPayload = useCallback(
    (fEntityTypeId?: FilterEntityEnum, fEntityIds?: number | number[]) => {
      // Contractor ko bypass karein
      if (isEntity || isContractor) return true;

      const resolvedType = fEntityTypeId ?? appliedFilters.filterEntityTypeId;
      const resolvedIds = fEntityIds ?? appliedFilters.filterEntityIds;
      const isPropertyOrUnit =
        Number(resolvedType) === FilterEntityEnum.Property ||
        Number(resolvedType) === FilterEntityEnum.Unit;
      const isIdMissing =
        !resolvedIds ||
        (Array.isArray(resolvedIds) && resolvedIds.length === 0);
      if (isPropertyOrUnit && isIdMissing) {
        const entityName =
          Number(resolvedType) === FilterEntityEnum.Property
            ? "Property"
            : "Unit";
        PubSub.publish(TopicsEnum.Notification.toString(), {
          type: NotificationType.Failure,
          message: `Please select at least one ${entityName} to proceed.`,
        });
        return false;
      }
      return true;
    },
    [isEntity, isContractor, appliedFilters]
  );

  // ── Date params (memoised so they don't recreate on every render) ───────────
  const dateParams = useMemo(
    () => buildDateParams(relativeDateString, range),
    [relativeDateString, range]
  );

  // ── Flat view — infiniteScrollHook ─────────────────────────────────────────
  const {
    rows,
    hasMore,
    fetchData,
    currentPage,
    calculateValue,
    aggregateValues,
    formattedAggregateValues,
    loading: loading1,
  } = infiniteScrollHook({
    httpFetchDataFn: isContractorSelfView
      ? OrganizationService.getApiOrganizationTransactionContractorlist
      : !orgId || isNaN(orgId) || entityTypeId === EntityType.Tenant
        ? OrganizationService.getApiOrganizationTransactionTenantlist
        : OrganizationService.getApiOrganizationTransactionOrganizationlist,

    FullyQualifiedFilters: appliedFilters.fullyQualifiedFilters,
    ...dateParams,
    filterEntityTypeId: appliedFilters.filterEntityTypeId,
    filterEntityIds: appliedFilters.filterEntityIds,
  });

  // ── Static filter options (categories / payee / payment method lists) ───────
  useEffect(() => {
    if (entityTypeId === EntityType.Tenant || isContractorSelfView) return;
    apiHelper(
      OrganizationService.getApiOrganizationTransactionOrganizationFilters,
      { showNotification: false },
      orgId
    ).then((res) => {
      if (res?.data?.success) setFilterData(res.data.success);
    });
  }, []); // run once

  useEffect(() => {
    if (entityTypeId === EntityType.Tenant || isContractorSelfView) return;
    apiHelper(
      PaymentService.getApiPaymentMethodOrganizationlist,
      { showNotification: false },
      orgId
    ).then((res) => {
      if (res?.data?.success) setPaymentMethodsArr(res.data.success);
    });
    apiHelper(
      PaymentService.getApiPayeeAccountOrganizationlist,
      { showNotification: false },
      orgId
    ).then((res) => {
      if (res?.data?.success) setPayeeAccountData(res.data.success);
    });
  }, [entityTypeId, isContractorSelfView, orgId]);

  // ── Grouped view fetch functions ───────────────────────────────────────────

  const organizationsTXResponse = useCallback(
    (overrideFilters?: typeof appliedFilters) => {
      const filters = overrideFilters ?? appliedFilters;
      if (!validateApiPayload(filters.filterEntityTypeId, filters.filterEntityIds)) return;
      setLoading(true);
      apiHelper(
        OrganizationService.getApiOrganizationTransactionOrganizationlistTxGroups,
        { showNotification: false },
        orgId,
        undefined,
        undefined,
        undefined,
        filters.filterEntityTypeId,
        filters.filterEntityTypeId === FilterEntityEnum.Organization
          ? orgId
          : filters.filterEntityIds,
        filters.fullyQualifiedFilters,
        undefined,
        ...Object.values(buildDateParams(relativeDateString, range)) as any[]
      )
        .then((res) => {
          if (res?.data?.success) setOrganizationDataTX(res.data.success);
        })
        .finally(() => setLoading(false));
    },
    [appliedFilters, orgId, relativeDateString, range, validateApiPayload]
  );

  const organizationsResponse = useCallback(
    (id: number, overrideFilters?: typeof appliedFilters) => {
      const filters = overrideFilters ?? appliedFilters;
      if (!validateApiPayload(filters.filterEntityTypeId, filters.filterEntityIds)) return;
      setOrgLoading(true);
      apiHelper(
        OrganizationService.getApiOrganizationTransactionOrganizationlist,
        { showNotification: false },
        id,
        undefined,
        undefined,
        undefined,
        isEntity ? entityTypeId : filters.filterEntityTypeId,
        isEntity
          ? entityId
          : filters.filterEntityTypeId === FilterEntityEnum.Organization
            ? orgId
            : filters.filterEntityIds,
        filters.fullyQualifiedFilters,
        undefined,
        ...Object.values(buildDateParams(relativeDateString, range)) as any[]
      )
        .then((res) => {
          if (res?.data?.success) setOrganizationData(res.data.success);
        })
        .finally(() => setOrgLoading(false));
    },
    [appliedFilters, orgId, isEntity, entityId, entityTypeId, relativeDateString, range, validateApiPayload]
  );

  const propertyResponse = useCallback(
    (id: number | null, overrideFilters?: typeof appliedFilters) => {
      if (!id) return;
      const filters = overrideFilters ?? appliedFilters;
      setPropertyLoading(true);
      apiHelper(
        OrganizationService.getApiOrganizationTransactionPropertylist,
        { showNotification: false },
        id,
        undefined,
        undefined,
        undefined,
        filters.filterEntityTypeId,
        filters.filterEntityIds,
        filters.fullyQualifiedFilters,
        undefined,
        ...Object.values(buildDateParams(relativeDateString, range)) as any[]
      )
        .then((res) => {
          if (res?.data?.success) setPropertyData(res.data.success);
        })
        .finally(() => setPropertyLoading(false));
    },
    [appliedFilters, relativeDateString, range]
  );

  const unitResponse = useCallback(
    (id: number | null, overrideFilters?: typeof appliedFilters) => {
      if (!id) return;
      const filters = overrideFilters ?? appliedFilters;
      setUnitLoading(true);
      apiHelper(
        OrganizationService.getApiOrganizationTransactionUnitlist,
        { showNotification: false },
        id,
        undefined,
        undefined,
        undefined,
        filters.filterEntityTypeId,
        filters.filterEntityIds,
        filters.fullyQualifiedFilters,
        undefined,
        ...Object.values(buildDateParams(relativeDateString, range)) as any[]
      )
        .then((res) => {
          if (res?.data?.success) setUnitData(res.data.success);
        })
        .finally(() => setUnitLoading(false));
    },
    [appliedFilters, relativeDateString, range]
  );

  const propertyResponseTX = useCallback(
    (id: number | null, overrideFilters?: typeof appliedFilters) => {
      if (!id) return;
      const filters = overrideFilters ?? appliedFilters;
      apiHelper(
        OrganizationService.getApiOrganizationTransactionPropertylistTxGroups,
        { showNotification: false },
        id,
        undefined,
        undefined,
        undefined,
        filters.filterEntityTypeId,
        filters.filterEntityIds,
        filters.fullyQualifiedFilters,
        undefined,
        ...Object.values(buildDateParams(relativeDateString, range)) as any[]
      ).then((res) => {
        if (res?.data?.success) setPropertyDataTX(res.data.success);
      });
    },
    [appliedFilters, relativeDateString, range]
  );

  // ── Trigger grouped view fetch when filters/date change ────────────────────
  // `organizationsTXResponse` feeds the top-level group rows.
  // `organizationData` (flat list per org row) is fetched on-demand when the
  // user expands a row in TransactionTable — NOT on load or filter change.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (selectedView.name === "Grouped View") {
        organizationsTXResponse();
      }
      return;
    }
    if (selectedView.name === "Grouped View") {
      organizationsTXResponse();
      if (selectedPropertyId) {
        propertyResponseTX(selectedPropertyId);
        propertyResponse(selectedPropertyId);
      }
      if (selectedUnitId) unitResponse(selectedUnitId);
    } else if (selectedView.name === "Flat View") {
      fetchData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTrigger, selectedView.name]);

  // ── Handler: category checkbox toggle ─────────────────────────────────────
  const handleCategoryChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, column: string) => {
      const category = event.target.value;
      if (!category) return;
      setSelectedCategories((prev) => {
        const current = prev[column] ?? [];
        const updated = current.includes(category)
          ? current.filter((c) => c !== category)
          : [...current, category];
        const next = { ...prev };
        if (updated.length > 0) {
          next[column] = updated;
        } else {
          delete next[column];
        }
        return next;
      });
    },
    []
  );

  /**
   * Called by FilterDropdown when user clicks "Apply filter".
   * Receives the current selectedOption / selectedIds from context at the
   * moment of the click, avoiding stale-closure issues.
   */
  const handleApplyFilter = useCallback(
    (
      currentOption: FilterEntityEnum,
      currentIds: number | number[],
      currentCategories: SelectedCategories
    ) => {
      lastAppliedCategories.current = currentCategories;
      const fqf = buildFQF(currentCategories, isContractor && !isContractorSelfView, contractorPayeeAccountNames);

      // Use entity context if available, otherwise use what was selected in the dropdown
      const entityTypeIdResolved = isContractor ? undefined : (isEntity
        ? (entityTypeId as FilterEntityEnum)
        : (currentOption === FilterEntityEnum.Organization
          ? FilterEntityEnum.Organization
          : currentOption));

      const entityIdsResolved = isContractor ? undefined : (isEntity
        ? entityId
        : (currentOption === FilterEntityEnum.Organization
          ? orgId
          : currentIds));

      const newFilters = {
        fullyQualifiedFilters: fqf,
        filterEntityTypeId: entityTypeIdResolved,
        filterEntityIds: entityIdsResolved,
      };

      setAppliedFilters(newFilters);
      setFetchTrigger(t => t + 1);
    },
    [
      isContractor,
      contractorPayeeAccountNames,
      orgId,
    ]
  );

  /**
   * Called by FilterDropdown / CustomRangeFilter "Clear filter" action.
   */
  const resetFilters = useCallback(() => {
    setSelectedCategories({});
    lastAppliedCategories.current = {};
    setSelectedValues([]);
    setSelectedIds([]);
    setSelectedOption(FilterEntityEnum.Organization);
    setShow(false);

    const defaultFilters = {
      fullyQualifiedFilters: isContractor
        ? buildFQF({}, !isContractorSelfView, contractorPayeeAccountNames)
        : null,
      filterEntityTypeId: isContractor ? undefined : (isEntity
        ? (entityTypeId as FilterEntityEnum)
        : FilterEntityEnum.Organization),
      filterEntityIds: isContractor ? undefined : (isEntity
        ? entityId
        : orgId),
    };

    setAppliedFilters(defaultFilters);
    setFetchTrigger(t => t + 1);
  }, [
    isContractor,
    contractorPayeeAccountNames,
    isEntity,
    entityId,
    entityTypeId,
    orgId,
    setSelectedValues,
    setSelectedIds,
    setSelectedOption,
  ]);

  /**
   * Date filter apply — called from CustomRangeFilter's apply button.
   * In flat view the hook handles it via deps. In grouped view we call
   * the fetch functions directly.
   */
  const handleDateFilterApply = useCallback(() => {
    setFetchTrigger(t => t + 1);
  }, []);

  // ── View switcher ──────────────────────────────────────────────────────────
  const handleSelectView = useCallback(
    (view: { id: number; img: string; name: string }) => {
      setShow(false);
      setSelectedView(view);
      // Grouped view needs an initial load; flat view is driven by the hook.
      if (view.name === GROUPED_VIEW_NAME) {
        organizationsTXResponse();
      }
    },
    [organizationsTXResponse]
  );

  // ── Date range change (just update state — no fetch here) ─────────────────
  const onChangeHandler = useCallback((value: Range[]) => {
    setRange(value);
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  // The page-level spinner is a FIRST-LOAD-ONLY state. `infiniteScrollHook`
  // clears `rows` at the start of every page-1 fetch, so keying this purely off
  // `rows.length === 0` would unmount the entire page chrome (heading, date
  // range filter, filter dropdown, view switcher) on every filter/date apply and
  // drop the user's focus. Once any fetch has settled we keep the chrome mounted
  // and let the table bodies render their own in-place loading state.
  const hasLoadedOnceRef = useRef(false);
  const settled = !loading && !loading1;
  const isBusy = !hasLoadedOnceRef.current && !settled;
  useLayoutEffect(() => {
    if (settled) hasLoadedOnceRef.current = true;
  }, [settled]);

  const canShowExportButton = canManageTransactions;

  const handleExport = useCallback(async () => {
    if (!canShowExportButton || selectedView?.name !== FLAT_VIEW_NAME) return;

    const exportRequest = resolveTransactionExportRequest({
      isEntity,
      entityId,
      entityTypeId,
      orgId,
      appliedFilters,
      dateParams,
    });

    try {
      setIsExporting(true);
      const blob = await exportTransactionsToExcel(exportRequest);
      downloadBlob(blob, buildTransactionsExportFileName());
    } catch {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        message: EXPORT_FAILURE_MESSAGE,
      });
    } finally {
      setIsExporting(false);
    }
  }, [
    appliedFilters,
    canShowExportButton,
    dateParams,
    entityId,
    entityTypeId,
    isEntity,
    orgId,
    selectedView?.name,
  ]);

  return (
    <>
      {isBusy ? (
        <div
          className="d-flex justify-content-center align-items-center flex-grow-1 w-100"
          style={{ minHeight: "80vh" }}
        >
          <SimpleSpinner myClassName="Icon50" />
        </div>
      ) : (
        <div
          className={`transactionHeader border-bottom-0 d-flex flex-column ${tenatPadding} ${entityTypeId === EntityType.Tenant ? "" : (isOpen ? "sidebar-constrained" : "sidebar-collapsed")
            }`}
        >
          {/* ── Top bar ─────────────────────────────────────────────────── */}
          <div className="d-flex justify-content-between">
            <p className="top_header black">Transactions</p>
            <div className="monthhead gap-3 d-flex flex-row mx-4">
              <CustomRangeFilter
                range={range}
                selectedView={selectedView?.name}
                customRanges={customRanges}
                onChangeHandler={onChangeHandler}
                relativeDateString={relativeDateString}
                setRelativeDateString={setRelativeDateString}
                fetchData={handleDateFilterApply}   // grouped view manual call
                fetchData2={fetchData}              // flat view page-1 trigger
                ClearFilter={resetFilters}
                applyButton={true}
                setShow={setShow}
              />

              {entityTypeId !== EntityType.Tenant && !isContractor && (
                <CustomDropDownMenu
                  data={[]}
                  show={show}
                  onToggle={setShow}
                  Element={
                    <div
                      className="d-flex align-items-center normal_text gap-2 filter-btn"
                      onClick={() => setShow((s) => !s)}
                    >
                      <img
                        src={GetImages.Filter}
                        className="iconstyle black-white-icon"
                        alt="Filter"
                      />
                      <p>Filter</p>
                    </div>
                  }
                  content={
                    <FilterDropdown
                      fetchData={fetchData}
                      setShow={setShow}
                      filters={filterData}
                      selectedCategories={selectedCategories}
                      handleCategoryChange={handleCategoryChange}
                      propertyResponse={() => propertyResponse(selectedPropertyId)}
                      unitResponse={() => unitResponse(selectedUnitId)}
                      resetFilters={resetFilters}
                      organizationsTXResponse={organizationsTXResponse}
                      propertyResponseTX={() =>
                        propertyResponseTX(selectedPropertyId)
                      }
                      onApplyFilter={handleApplyFilter}
                      selectedView={selectedView}
                      isEntity={isEntity}
                      entityId={entityId}
                      isContractor={isContractor}
                    />
                  }
                />
              )}

              {!isEntity && !isContractor && (
                <CustomDropDownMenu
                  Element={
                    <AddTraspButton
                      className="black normal_text black-white-icon"
                      bname={selectedView?.name}
                      imageLeft={
                        selectedView?.name === FLAT_VIEW_NAME
                          ? GetImages.FlatViewIcon
                          : GetImages.GroupedView
                      }
                      imageRight={GetImages.BlackChevronDown}
                      both={true}
                    />
                  }
                  data={viewMenuData}
                  onSelect={handleSelectView}
                />
              )}

              {canShowExportButton && selectedView?.name === FLAT_VIEW_NAME && (
                <button
                  type="button"
                  className="d-flex align-items-center justify-content-center filter-btn export-excel-btn"
                  onClick={handleExport}
                  disabled={isExporting}
                  aria-label="Export transactions to Excel"
                  title="Export to Excel"
                >
                  {isExporting ? (
                    <SimpleSpinner myClassName="Icon20" />
                  ) : (
                    <img
                      src={GetImages.ExcelIcon}
                      className="Icon20"
                      alt="Export to Excel"
                    />
                  )}
                </button>
              )}
            </div>
          </div>

          {/* ── Table area ──────────────────────────────────────────────── */}
          {selectedView?.name === FLAT_VIEW_NAME ? (
            <FlatViewTable
              payeeAccountData={payeeAccountData}
              paymentMethodsArr={paymentMethodsArr}
              rows={rows}
              loading={loading}
              loading1={loading1}
              hasMore={hasMore}
              fetchData={fetchData}
              currentPage={currentPage}
              calculateValue={calculateValue}
              aggregateValues={aggregateValues}
              formattedAggregateValues={formattedAggregateValues}
              entityTypeId={entityTypeId}
              isContractorSelfView={isContractorSelfView}
              canManageTransactions={canManageTransactions}
              onRefresh={onRefresh}
            />
          ) : (
            <TransactionTable
              organizationDataTX={organizationDataTX}
              organizationData={organizationData}
              propertyData={propertyData}
              unitData={unitData}
              setOrganizationData={setOrganizationData}
              setPropertyData={setPropertyData}
              setUnitData={setUnitData}
              propertyDataTX={propertyDataTX}
              organizationsResponse={organizationsResponse}
              propertyResponse={propertyResponse}
              unitResponse={unitResponse}
              selectedPropertyId={selectedPropertyId}
              selectedUnitId={selectedUnitId}
              setSelectedPropertyId={setSelectedPropertyId}
              setSelectedUnitId={setSelectedUnitId}
              propertyResponseTX={propertyResponseTX}
              organizationsTXResponse={organizationsTXResponse}
              orgLoading={orgLoading}
              unitLoading={unitLoading}
              propertyLoading={propertyLoading}
              loading={loading}
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
              payeeAccountData={payeeAccountData}
              paymentMethodsArr={paymentMethodsArr}
              canManageTransactions={canManageGroupedTransactions}
              onApplyFilter={handleApplyFilter}
              resetFilters={resetFilters}
              onRefresh={onRefresh}
            />
          )}
        </div>
      )}
    </>
  );
};

export default TransactionView;